// frontend/components/AdminFormTApproval.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Avatar,
  Stack
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Description as FormIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Lock as LockIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from '@mui/icons-material';


import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminFormTApproval = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [typeFilter, setTypeFilter] = useState('all');
  const [processing, setProcessing] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [isFinanceUser, setIsFinanceUser] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    recovered: 0
  });

  useEffect(() => {
    checkUserRole();
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [statusFilter, typeFilter, applications]);

  const checkUserRole = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const role = payload.role || payload.userRole || payload.roleType || 'employee';
        setUserRole(role);
        setIsFinanceUser(role === 'finance' || role === 'admin');
      }
    } catch (error) {
      //console.error('Error checking user role:', error);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications);
      calculateStats(response.data.applications);
      setLoading(false);
    } catch (error) {
      //console.error('Error fetching applications:', error);
      setError(error.response?.data?.error || 'Failed to fetch applications');
      setLoading(false);
    }
  };

  const calculateStats = (apps) => {
    const stats = {
      pending: apps.filter(app => app.status === 'PENDING' || app.status === 'UNDER_REVIEW').length,
      approved: apps.filter(app => app.status === 'APPROVED').length,
      rejected: apps.filter(app => app.status === 'REJECTED').length,
      recovered: apps.filter(app => app.status === 'RECOVERED').length
    };
    setStats(stats);
  };

  const filterApplications = () => {
    let filtered = [...applications];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(app => app.applicant_type === typeFilter);
    }
    
    setFilteredApps(filtered);
  };

  const handleApprove = async (id) => {
    if (!isFinanceUser) {
      alert('Only Finance department can approve applications.');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${API_URL}/api/form-t/admin/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Application approved successfully!');
        fetchApplications();
        setViewDialogOpen(false);
      }
    } catch (error) {
      //console.error('Error approving application:', error);
      alert(error.response?.data?.error || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!isFinanceUser) {
      alert('Only Finance department can reject applications.');
      return;
    }
    
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/form-t/admin/${selectedApp.id}/reject`,
        { rejection_reason: rejectReason },{
         headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Application rejected successfully!');
        setRejectDialogOpen(false);
        setRejectReason('');
        fetchApplications();
        setViewDialogOpen(false);
      }
    } catch (error) {
      //console.error('Error rejecting application:', error);
      alert(error.response?.data?.error || 'Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, `Form_T_${id}.pdf`);
    } catch (error) {
      //console.error('Error downloading Form T:', error);
      alert(error.response?.data?.error || 'Failed to download Form T');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Draft', color: 'default', icon: null },
      PENDING: { label: 'Pending', color: 'warning', icon: null },
      UNDER_REVIEW: { label: 'Under Review', color: 'info', icon: null },
      APPROVED: { label: 'Approved', color: 'success', icon: <ApproveIcon /> },
      REJECTED: { label: 'Rejected', color: 'error', icon: null },
      RECOVERED: { label: 'Recovered', color: 'primary', icon: null }
    };
    const config = statusConfig[status] || { label: status, color: 'default', icon: null };
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
  };

  const getApplicantTypeLabel = (type) => {
    const types = {
      employee: 'Self (Employee)',
      nominee: 'Nominee',
      legal_heir: 'Legal Heir'
    };
    return types[type] || type;
  };

  // Show access denied if not finance user
  if (!isFinanceUser && userRole) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This page is restricted to Finance department only.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please contact your administrator if you believe this is a mistake.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>FormT Approval</Typography>
      {/* Finance Department Header */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Finance Department Portal</strong> - You have access to approve/reject Form T applications.
      </Alert>


{/* Stats Cards */}
<Grid container spacing={3} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        bgcolor: "#fff",
        borderLeft: "6px solid #ff9800",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          color="warning.main"
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <PendingIcon />
          {stats.pending}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Pending Applications
        </Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        bgcolor: "#fff",
        borderLeft: "6px solid #4caf50",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          color="success.main"
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CheckCircleIcon />
          {stats.approved}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Approved Applications
        </Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        bgcolor: "#fff",
        borderLeft: "6px solid #f44336",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          color="error.main"
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CancelIcon />
          {stats.rejected}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Rejected Applications
        </Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        bgcolor: "#fff",
        borderLeft: "6px solid #1976d2",
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          color="primary.main"
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CurrencyRupeeIcon />
          {stats.recovered}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Recovered Amounts
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="RECOVERED">Recovered</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Applicant Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Applicant Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="employee">Employee/Self</MenuItem>
                <MenuItem value="nominee">Nominee</MenuItem>
                <MenuItem value="legal_heir">Legal Heir</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchApplications}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell><strong>Application No.</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Applicant Type</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApps.map((app) => (
              <TableRow key={app.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {app.application_no}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(app.application_date).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                      {app.employee?.firstName?.charAt(0) || 'E'}
                    </Avatar>
                    <Typography variant="body2">
                      {app.employee?.firstName} {app.employee?.lastName}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getApplicantTypeLabel(app.applicant_type)} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    ₹ {app.gratuity_amount?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  {getStatusChip(app.status)}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => {
                        setSelectedApp(app);
                        setViewDialogOpen(true);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  
                  {app.status === 'APPROVED' && (
                    <Tooltip title="Download Form T">
                      <IconButton 
                        size="small" 
                        color="success"
                        onClick={() => handleDownload(app.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Application Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Application Details
          </Typography>
          <IconButton onClick={() => setViewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedApp && (
            <Box>
              {/* Application Header */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Application No.
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedApp.application_no}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Application Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedApp.application_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box>{getStatusChip(selectedApp.status)}</Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Gratuity Amount
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      ₹ {selectedApp.gratuity_amount?.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Employee Details */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employee Details
              </Typography>
              <Box sx={{ mb: 3, pl: 2 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedApp.employee?.firstName} {selectedApp.employee?.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Employee ID:</strong> {selectedApp.employee_id}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedApp.employee?.employmentStatus}
                </Typography>
              </Box>

              {/* Applicant Details */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Applicant Details
              </Typography>
              <Box sx={{ mb: 3, pl: 2 }}>
                <Typography variant="body2">
                  <strong>Type:</strong> {getApplicantTypeLabel(selectedApp.applicant_type)}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedApp.applicant_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Address:</strong> {selectedApp.applicant_address}
                </Typography>
                <Typography variant="body2">
                  <strong>Relationship:</strong> {selectedApp.relationship}
                </Typography>
              </Box>

              {/* Documents */}
              {(selectedApp.legal_heir_document || selectedApp.death_certificate) && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    <PdfIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Uploaded Documents
                  </Typography>
                  <Box sx={{ mb: 3, pl: 2 }}>
                    {selectedApp.death_certificate && (
                      <Typography variant="body2">
                        <strong>Death Certificate:</strong> {selectedApp.death_certificate}
                      </Typography>
                    )}
                    {selectedApp.legal_heir_document && (
                      <Typography variant="body2">
                        <strong>Legal Heir Certificate:</strong> {selectedApp.legal_heir_document}
                      </Typography>
                    )}
                    {selectedApp.legal_heir_id_proof && (
                      <Typography variant="body2">
                        <strong>Identity Proof:</strong> {selectedApp.legal_heir_id_proof}
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* Rejection Reason if any */}
              {selectedApp.status === 'REJECTED' && selectedApp.rejection_reason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Rejection Reason:</strong> {selectedApp.rejection_reason}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {selectedApp?.status === 'PENDING' && isFinanceUser && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => setRejectDialogOpen(true)}
                disabled={processing}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleApprove(selectedApp.id)}
                disabled={processing}
              >
                Approve
              </Button>
            </>
          )}
          {selectedApp?.status === 'APPROVED' && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedApp.id)}
            >
              Download Form T
            </Button>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject Application
          <IconButton
            onClick={() => setRejectDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this application.
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., Insufficient documents, Ineligible applicant, etc."
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing || !rejectReason.trim()}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFormTApproval;